import React from 'react';

import { makeStyles } from 'tss-react/mui';

import { Imgix } from 'ha/modules/Image/Imgix';
import { ImgixSize } from 'ha/modules/Image/constants';
import { Grid, Button, Typography, units } from '@hbf/dsl';

export interface PreviewTileProps {
  backgroundImage: string;
  icon?: React.ReactNode;
  title?: string;
  type?: 'default' | 'top' | 'centralized' | 'dark' | 'light';
  onClick: () => void;
  dataTestLocator?: string;
  className?: string;
}

const useStyles = makeStyles()(theme => ({
  container: {
    left: units.rem(0),
    height: '100%',
    position: 'absolute',
    top: units.rem(0),
    width: '100%',
    zIndex: 2,
  },
  icon: {
    fontSize: units.rem(1.188),
    lineHeight: units.rem(1.5),
    fontWeight: 'bold',
  },
  iconDefault: {
    color: theme.palette.common.white,
  },
  iconPurple: {
    color: theme.palette.primary.dark,
  },
  iconWhite: {
    color: theme.palette.common.white,
  },
  overlay: {
    left: units.rem(0),
    height: '100%',
    position: 'absolute',
    top: units.rem(0),
    width: '100%',
  },
  overlayGradient: {
    background:
      'linear-gradient(180deg, rgba(0, 0, 0, 0.4) 0%, rgba(0, 0, 0, 0) 65.82%)',
    padding: units.rem(0.675),
  },
  overlayGradientTitle: {
    background:
      'linear-gradient(179.3deg, rgba(0, 0, 0, 0.2) 21.86%, rgba(0, 0, 0, 0) 99.39%)',
    paddingLeft: units.rem(0.625),
    paddingTop: units.rem(0.625),
  },
  overlayLight: {
    background: theme.palette.grey[100],
    border: units.border(1, 'solid', theme.palette.grey[200]),
    padding: units.rem(1.5),
  },
  overlayOpaque: {
    background:
      'linear-gradient(0deg, rgba(45, 70, 88, 0.7), rgba(45, 70, 88, 0.7))',
    height: '100%',
    left: 0,
    opacity: 1,
    padding: units.rem(1.5),
    position: 'absolute',
    top: 0,
    width: '100%',
  },
  img: {
    width: '100%',
    height: 'auto',
    objectFit: 'cover',
  },
  tile: {
    borderRadius: units.rem(0.25),
    display: 'inline-block',
    overflow: 'hidden',
    position: 'relative',
    padding: 0,
    textTransform: 'none',
  },
  title: {
    lineHeight: units.rem(1.125),
  },
  titleContainer: {
    paddingLeft: units.rem(0.25),
    paddingRight: units.rem(0.25),
    width: '100%',
  },
  titleContainerTop: {
    marginLeft: units.rem(0.25),
    marginRight: units.rem(0.25),
    zIndex: 8,
  },
}));

const PreviewTile: React.FC<PreviewTileProps> = ({
  backgroundImage,
  icon,
  title,
  type = 'default',
  onClick,
  dataTestLocator,
  className,
}) => {
  const { classes, cx } = useStyles();

  let gradientClass = classes.overlayOpaque;
  if (type === 'top')
    gradientClass = cx(classes.overlay, classes.overlayGradient);
  if (type === 'light')
    gradientClass = cx(classes.overlay, classes.overlayLight);

  const iconColor = type === 'dark' ? classes.iconWhite : classes.iconPurple;
  const titleColor = type === 'dark' ? 'white' : 'primary';

  return (
    <Button
      variant="text"
      className={cx(classes.tile, className)}
      type="button"
      onClick={onClick}
      data-test-locator={dataTestLocator}
    >
      {type === 'top' && (
        <Grid
          container
          direction="row"
          alignItems="flex-start"
          className={cx(classes.overlay, classes.overlayGradientTitle)}
        >
          {icon && (
            <Grid item className={classes.iconDefault}>
              {icon}
            </Grid>
          )}
          <Grid item className={classes.titleContainerTop}>
            <Typography.Caption color="white">{title}</Typography.Caption>
          </Grid>
        </Grid>
      )}

      {type === 'centralized' && (
        <Grid
          container
          alignItems="center"
          justifyContent="center"
          className={classes.overlay}
        >
          <Grid item className={classes.titleContainerTop}>
            <Typography.H4
              fontWeight="normal"
              color="white"
              textTransform="none"
              data-test-locator={title}
            >
              {title}
            </Typography.H4>
          </Grid>
        </Grid>
      )}

      {(type === 'dark' || type === 'light') && (
        <Grid
          container
          direction="column"
          alignItems="center"
          justifyContent="center"
          className={classes.container}
          data-test-locator={title}
        >
          {icon && (
            <Grid item className={cx(classes.icon, iconColor)}>
              {icon}
            </Grid>
          )}
          <Grid item className={classes.titleContainer}>
            <Typography.Caption
              fontWeight="bold"
              color={titleColor}
              textTransform="none"
              className={classes.title}
            >
              {title}
            </Typography.Caption>
          </Grid>
        </Grid>
      )}

      <Imgix
        htmlAttributes={{ alt: 'preview gallery tile' }}
        src={backgroundImage}
        className={classes.img}
        width={ImgixSize.xxsmall.w}
        height={ImgixSize.xxsmall.h}
        imgixParams={{ fit: 'crop', cs: 'tinysrgb' }}
      />

      {type !== 'default' && <div className={gradientClass} />}
    </Button>
  );
};

export { PreviewTile };
